import type { InputType, Stage } from './stage';
import type { UndoRef } from './undo';
import type { Department } from '../user/user';
import type { DeletionStatus } from '../order/deletion';
import type { Origin } from '../order/origin';
import type { Span } from '../teeth/spans';
import type { Permission } from '../access/role';
import { isLate } from '../time/dueDate';

/** One tooth on the chart and the catalogue item being made for it. */
export interface ToothItem {
  tooth: string; // FDI number, e.g. "14"
  product: string; // catalogue item name, exactly as in the price list
  /** The catalogue item's id, which survives a rename. */
  catalogItemId: string;
}

/** One catalogue item and every tooth on the case that calls for it. */
export interface ToothItemGroup {
  product: string;
  teeth: string[];
}

/**
 * The items on a case, gathered one entry per distinct item.
 *
 * The order is first appearance, which is the order the teeth were marked at
 * intake, so a sheet printed from this reads down in the same order as the
 * chart beside it. Teeth within an entry keep that order too.
 *
 * A case that is all one item gives exactly one entry, which is most cases and
 * the reason a caller can render this without special casing the common shape.
 * A case with nothing marked gives none, and the caller shows the case's own
 * item instead: a case opened on a unit count over the phone has an item and no
 * teeth to hang it on yet.
 *
 * This is what the tray sheet has to print. A bench handed only the case's
 * headline item would make Zircon crowns on the two teeth that were meant to be
 * E.max veneers, and nothing downstream would catch it until Final Check.
 */
export function groupToothItems(toothItems: readonly ToothItem[]): ToothItemGroup[] {
  const groups: ToothItemGroup[] = [];

  for (const item of toothItems) {
    const existing = groups.find((group) => group.product === item.product);
    if (existing === undefined) groups.push({ product: item.product, teeth: [item.tooth] });
    else existing.teeth.push(item.tooth);
  }

  return groups;
}

/**
 * A kit is one physical tray, holding one restoration job, carrying a printed
 * sheet with a QR code. Section 4.2.
 */
export interface Kit {
  id: string; // "K-1084"
  qrCode: string; // "QR-47312", the value encoded in the printed QR
  jobOrderNo: string; // "JO-2041"
  stage: Stage;
  input: InputType;
  urgent: boolean;
  rework: boolean; // came back from Final Check
  /** The order is on hold: nobody works on it until it is released. */
  held: boolean;
  /** Why it is held, while it is. */
  holdReason: string | null;
  product: string; // catalogue item name, exactly as in the price list
  /** The catalogue item, by id: what a case keeps when the item is renamed. */
  catalogItemId: string;
  teeth: string[]; // FDI numbers as strings, e.g. ["14","15","16"]
  /**
   * The item on each tooth, in the same order as `teeth`.
   *
   * A case is regularly not one item: six units for one patient is often three
   * Zircon crowns, two E.max veneers and a post. `product` above is the case's
   * headline item, which is what the order lists show and what the production
   * route is chosen from; this is what is actually being made, tooth by tooth.
   *
   * `product` here is never empty. A tooth recorded before this existed takes
   * the case's item, and the server resolves that before it sends.
   */
  toothItems: ToothItem[];
  /**
   * How many teeth this tray covers. Not `teeth.length`: a case can be opened
   * on a count alone, before anybody has marked the chart. Where the chart is
   * filled in the two agree.
   */
  units: number;
  /** What the case was priced at when it was taken in. Absent without pay or admin. */
  unitPriceAtOrder?: number | null;
  /**
   * The bridges and titanium bars on the case, projected from the order the
   * way `held` is, so a bench reading a tray sees them without a second call.
   */
  spans: Span[];
  shade: string; // "A2"
  specification: KitSpecification;
  itemsReceived: ItemsReceived;
  /**
   * The instructions on the case: one field, since section 5 of the request of
   * 5 October 2026 merged the lab's own into it. `instructions.ts`.
   */
  instructions: string;
  assignedToUserId: string | null;
  /** The assignee's name, so no screen has to resolve an id. Null when unassigned. */
  assignedToUserName: string | null;
  /**
   * Who gave the case to the person holding it, for the take-over warning: the
   * assigner, or the holder themselves when they took it by scanning. Null when
   * nobody holds it or nobody recorded it. Section 9 of the request of
   * 5 October 2026. Optional, for a response from a build that does not send it.
   */
  assignedByUserId?: string | null;
  assignedByUserName?: string | null;
  /**
   * The caller's own last action on this case while it can still be undone.
   * Null, or absent, for everybody else and once the moment has passed. Section
   * 6 of the request of 5 October 2026; `undo.ts`.
   */
  undo?: UndoRef | null;
  origin: Origin; // where the case comes from, projected from the order
  daysLeft: number; // working days to delivery, may be negative
  minutesInStage: number;

  /**
   * Identity fields. ABSENT from the payload for any caller without
   * `seeIdentity`, section 7.10 item 1. Absent at the source, not blanked out
   * later. Every screen must render correctly when these are null.
   */
  dentistName: string | null;
  patientName: string | null;
  /**
   * The doctor's and the patient's records, by id, projected from the order.
   * Sent only where the two names are: absent without `seeIdentity`, and null
   * on a case that was never linked to a record. The request of
   * 6 October 2026 (second), items 4 and 5.
   */
  doctorId?: string | null;
  patientId?: string | null;

  sentAt: string | null; // ISO 8601 UTC
  startDate: string; // ISO 8601 date
  deliveryDate: string; // ISO 8601 date
  /**
   * When the case arrived at the lab: the moment its job order was booked in,
   * projected from the order so the Final Check list shows it without a call a
   * row. The request of 7 October 2026, section 4. Absent from an API older
   * than the field, and null for a tray with no order behind it.
   */
  receivedAt?: string | null;
  /**
   * The job order this case remakes, and the doctor's clinic, projected from
   * the order: two columns a bench and Final Check can switch on. The lab's
   * request of 11 October 2026. The clinic is identity, absent with the names.
   */
  redoOf?: string | null;
  clinic?: string | null;
  createdByUserId: string | null;
  photoCount: number;
  noteCount: number;
  /**
   * The deletion on the kit's order, projected from it, so the job view can say
   * a deletion is waiting without a second call. A deleted case's kit is not
   * served at all, so this is `none` or `pending` in practice.
   */
  deletionStatus: DeletionStatus;
  /**
   * The order is archived, projected as the deletion is, so the job view can say
   * so and offer nothing to start. Section 4 of the request of 24 September 2026.
   */
  archived: boolean;
  rowVersion: string; // optimistic concurrency token, section 7.9
}

/**
 * How much of a property a case calls for.
 *
 * ONE scale for all four rows of the specification, ascending, spelled out.
 * The paper form used single letters and ran them in different directions on
 * different rows, which is how the client came to ask what one of them meant.
 * Words cannot be misread and one direction cannot be misordered.
 *
 * Occlusal stain shares it at the client's instruction. An earlier cut gave
 * stain its own four step scale starting at "none", on the reasoning that no
 * staining is a different instruction from a little; the client chose one
 * consistent scale instead. If "no staining at all" ever needs saying, it
 * belongs in the instructions field rather than as a fourth step here.
 */
export const SPEC_SCALE = ['low', 'medium', 'high'] as const;
export type SpecScale = (typeof SPEC_SCALE)[number];

export const SPEC_SCALE_LABEL: Record<SpecScale, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

/** The case specification, as it reads on the lab's paper job order. */
export interface KitSpecification {
  translucency: SpecScale;
  anatomy: SpecScale;
  incisal: SpecScale;
  occlusal: SpecScale;
  ingot: string; // "IPS e.max Press LT A2"
  block: string; // "Katana STML"
  stain: string;
}

/**
 * What physically arrived with the case, as five tick boxes. The write-in line
 * that sat beside them went with the request of 13 September 2026, section 3:
 * intake has one notes field, and that is the instructions.
 */
export interface ItemsReceived {
  impression: boolean;
  model: boolean;
  bite: boolean;
  implant: boolean;
  abutment: boolean;
}

export type ItemReceivedKey = keyof ItemsReceived;

export const ITEMS_RECEIVED_LABEL: Record<ItemReceivedKey, string> = {
  impression: 'Impression',
  model: 'Model',
  bite: 'Bite record',
  implant: 'Implant analogue',
  abutment: 'Abutment',
};

export interface StageHistoryEntry {
  id: string;
  /**
   * The tray the entry is of, so the steps of a case with several can be
   * followed one tray at a time. The request of 7 October 2026, section 4.
   * Absent from an API older than the field.
   */
  kitId?: string;
  stage: Stage;
  byUserId: string | null;
  byUserName: string | null;
  startedAt: string; // ISO 8601 UTC
  endedAt: string | null; // null means still in progress
  activeSeconds: number; // TIME ACTUALLY WORKED, not wall clock
  note: string | null; // e.g. "Failed QC", flag reason
}

/**
 * One note about a case, whatever it was stored as. The four extra fields answer
 * the client's four questions: what kind of note it is, where it came from and
 * who it is for, beside what it says and who wrote it. Section 3 of the request
 * of 16 September 2026.
 */
export interface KitNote {
  id: string;
  /** Null for a note the lab wrote rather than a person, such as a hold reason. */
  byUserId: string | null;
  byUserName: string;
  text: string;
  createdAt: string;
  kind: NoteKind;
  /** The stage it was written at. Null for an order-level note such as a hold. */
  fromStage: Stage | null;
  /** The stage it is addressed to. Set on a rework or a flag; null otherwise. */
  toStage: Stage | null;
  /**
   * The system's own sentence for an event in Lab Notes, "Assigned to Jessica";
   * null for a note somebody wrote. Request of 4 October 2026, section 7.
   */
  event: string | null;
  /** Where the writer works, shown beside their name in Lab Notes. */
  byUserDepartment: Department | null;
  /**
   * The one person a private note is for, or null when everybody on the case
   * sees it. The server never sends a private note to anybody else.
   */
  visibleTo: { id: string; name: string } | null;
  /**
   * When the action this event records was undone, and by whom. The entry
   * stays, marked, so the history is honest. Null, or absent, on one that
   * stands. Section 6 of the request of 5 October 2026.
   */
  undoneAt?: string | null;
  undoneByName?: string | null;
  /**
   * The stage visit an event belongs to: on a proof row, the one whose
   * photographs it counts. Null, or absent, on a note somebody wrote and on a
   * row read off the order. The request of 5 October 2026 (third), section 6.
   */
  historyEntryId?: string | null;
}

/** A case's Lab Notes and how far this person has read them. */
export interface NoteThread {
  /** Newest first, as the server sends them. */
  items: KitNote[];
  lastReadAt: string | null;
}

/**
 * The two kinds of photograph on a case. A case photograph is what reception
 * took at intake and what the bench added on the way through; a proof is a
 * photograph of the finished work, taken at Mark done against the piece of work
 * it proves, arranged apart from the case photographs and read through its own
 * permission. Section 2 of the request of 13 September 2026.
 */
/**
 * The kinds of note a case collects. Section 3 of the request of
 * 16 September 2026.
 *
 * Notes about a case were written in five places and stored in two shapes: a
 * free note in its own row, a rework or flag reason on the history entry it
 * closed, a hold reason on the order, and a deletion or redo reason in their own
 * blocks. Scattered by STORAGE rather than gathered by MEANING, and none of them
 * recorded who the note was for.
 *
 * This is the vocabulary of the read model that gathers them. Nothing is copied:
 * a rework reason still lives on its history entry, which is what the Sent back
 * from Final Check banner reads and what confirmation 1 of the system test
 * handover depends on. Two records of one sentence would drift.
 */
/**
 * Taking a case photograph off a case. Section 3 of the request of
 * 16 September 2026: reception photographs what arrived in the box, and what
 * arrived in the box is sometimes photographed twice, sideways, or with a
 * finger over it.
 *
 * A PROOF OF WORK is not a case photograph and cannot be removed. It is the
 * record that a stage was finished, taken at the moment it was finished, and a
 * record somebody can delete when it becomes inconvenient is not a record.
 */
export const PHOTO_REFUSAL = {
  notAllowed: 'You cannot change the photographs on a case.',
  notHere: 'That photograph is not on this case',
  isWorkProof: 'A proof of work cannot be removed.',
  /** Download without `downloadPhotos`. The request of 5 October 2026 (third), section 6. */
  downloadNotAllowed: 'You cannot download photographs.',
} as const;

/**
 * Whether somebody is offered Download in the image viewer: a holder of
 * `downloadPhotos`, whatever their role. The server asks the same of the
 * route, so the button is never there for a call that would be refused. Twin
 * of `PhotoRules.MayDownload`. The request of 5 October 2026 (third),
 * section 6.
 */
export function mayDownloadPhoto(permissions: readonly Permission[]): boolean {
  return permissions.includes('downloadPhotos');
}

/**
 * A case on hold is a case the lab has stopped: nobody works on it until the
 * hold is released. Start, Mark done, Flag and Final Check are refused with
 * this, word for word as the server says it; Pause and a manager's Stop stay
 * open. Section 9 of the review of 2 October 2026.
 */
export const HOLD_REFUSAL = {
  onHold: 'This case is on hold.',
} as const;

export const NOTE_KINDS = [
  'note',
  'rework',
  'flag',
  'hold',
  'deletion',
  'redo',
  // System events in Lab Notes. Request of 4 October 2026, section 7.
  'done',
  'assign',
  'move',
  'resolved',
  'passed',
  // Proof of work photographed at Mark done, one row per stage visit. Section 6b.
  'proof',
  // The case left the lab, and the customer has it. Read off the order's
  // shipping when Lab Notes is asked for, as hold, deletion and redo are read
  // off the order, and never stored. The request of 5 October 2026 (third),
  // section 11.
  'shipped',
  'received',
] as const;

/**
 * What a Final Check rework writes in front of the reviewer's words when it
 * closes the history entry, and so what tells a rework reason from a flag
 * reason when the two are read back off that entry. Written down once here
 * because the command that writes it and the read model that strips it are in
 * different layers.
 */
export const QC_REWORK_PREFIX = 'Failed QC · ';

export type NoteKind = (typeof NOTE_KINDS)[number];

export const PHOTO_KINDS = ['case', 'proof'] as const;
export type PhotoKind = (typeof PHOTO_KINDS)[number];

export interface CasePhoto {
  id: string;
  url: string;
  thumbnailUrl: string;
  uploadedByUserId: string;
  /** Who took it. The id is already here; the name is what a screen shows. */
  uploadedByUserName: string;
  uploadedAt: string;
  stage: Stage;
  kind: PhotoKind;
  /** The piece of work a proof proves. Null on a case photograph. */
  historyEntryId: string | null;
}

/**
 * A photograph's address without the signature the server puts on it.
 *
 * Every list of photographs comes back freshly signed (`?exp=&sig=`), so the
 * same file has a different address each time it is listed. Whatever keeps a
 * photograph's bytes keeps them under this, or it fetches every picture again
 * after every refresh. The request of 5 October 2026 (third), section 6.
 */
export function photoPath(url: string): string {
  const cut = url.search(/[?#]/);
  return cut < 0 ? url : url.slice(0, cut);
}

const PHOTO_EXTENSION: Record<string, string> = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
};

/**
 * What a downloaded photograph is called on the device: the case, the stage
 * and the photograph, "JO-2047-ceramic-P-123.jpg". The server names the file
 * the same way, but a browser on another origin is not shown that header, so
 * the name is made here. Twin of `PhotoRules.DownloadName`. The request of
 * 5 October 2026 (third), section 6.
 *
 * The ending is what the bytes say they are; for a file that says nothing
 * useful, the ending it was stored under, or none.
 */
export function photoDownloadName(
  jobOrderNo: string | null | undefined,
  photo: Pick<CasePhoto, 'id' | 'stage' | 'url'>,
  contentType: string,
): string {
  const type = contentType.replace(/;.*$/, '').trim().toLowerCase();
  const path = photoPath(photo.url);
  const stored = /\.[a-z0-9]{1,5}$/i.test(path) ? path.slice(path.lastIndexOf('.')).toLowerCase() : '';
  return `${jobOrderNo ?? 'photo'}-${photo.stage}-${photo.id}${PHOTO_EXTENSION[type] ?? stored}`;
}

/** The proofs of one piece of work, in the order they were taken. */
export function proofsOfEntry(
  proofs: readonly CasePhoto[],
  historyEntryId: string,
): CasePhoto[] {
  return proofs.filter((photo) => photo.historyEntryId === historyEntryId);
}

/**
 * The proofs of a case by stage, in the order the stages were first
 * photographed, so a record reads down the case the way the case went. A case
 * that passed through Ceramic twice has two entries under one stage, which is
 * what the card wants to show.
 */
export function groupProofsByStage(
  proofs: readonly CasePhoto[],
): { stage: Stage; proofs: CasePhoto[] }[] {
  const groups: { stage: Stage; proofs: CasePhoto[] }[] = [];
  for (const photo of proofs) {
    const existing = groups.find((group) => group.stage === photo.stage);
    if (existing === undefined) groups.push({ stage: photo.stage, proofs: [photo] });
    else existing.proofs.push(photo);
  }
  return groups;
}

/**
 * A kit is overdue when the delivery date has passed and it has not reached
 * Ready. Overdue renders in the `late` tone, never in the urgent red.
 */
export function isOverdue(kit: Pick<Kit, 'deliveryDate' | 'stage'>, today: string): boolean {
  return isLate(kit.deliveryDate, today, kit.stage === 'ready');
}
